import React, { useState, useEffect } from 'react';
import type { CmsPage, CmsBlock } from '../../types';
import { BlockItem } from './BlockItem';
import { BlockToolbar } from './BlockToolbar';
import { PageSettingsModal } from './PageSettingsModal';
import { getAuthHeaders } from '../../lib/auth';
import {
  Save,
  RotateCcw,
  Undo,
  Redo,
  Eye,
  Edit2,
  Settings,
  Trash2,
  Plus,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

interface PageEditorProps {
  initialPage: CmsPage;
  allPages: CmsPage[];
  onSaveSuccess?: (savedPage: CmsPage) => void;
  onDeleteSuccess?: (deletedSlug: string) => void;
  onExit?: () => void;
}

export const PageEditor: React.FC<PageEditorProps> = ({
  initialPage,
  allPages,
  onSaveSuccess,
  onDeleteSuccess,
  onExit,
}) => {
  const [page, setPage] = useState<CmsPage>(initialPage);
  const [isPreview, setIsPreview] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  // History stack for Undo / Redo
  const [history, setHistory] = useState<CmsBlock[][]>([initialPage.body || []]);
  const [historyIndex, setHistoryIndex] = useState(0);

  useEffect(() => {
    setPage(initialPage);
    setHistory([initialPage.body || []]);
    setHistoryIndex(0);
  }, [initialPage.slug]);

  const updateBlocksWithHistory = (newBlocks: CmsBlock[]) => {
    const updatedHistory = history.slice(0, historyIndex + 1);
    updatedHistory.push(newBlocks);
    setHistory(updatedHistory);
    setHistoryIndex(updatedHistory.length - 1);
    setPage((prev) => ({ ...prev, body: newBlocks }));
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setPage((prev) => ({ ...prev, body: history[prevIndex] }));
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setPage((prev) => ({ ...prev, body: history[nextIndex] }));
    }
  };

  const handleAddBlock = (type: CmsBlock['type'], atIndex?: number) => {
    const newBlock: CmsBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      data: getDefaultBlockData(type),
    };

    const currentBlocks = [...page.body];
    if (typeof atIndex === 'number') {
      currentBlocks.splice(atIndex, 0, newBlock);
    } else {
      currentBlocks.push(newBlock);
    }

    updateBlocksWithHistory(currentBlocks);
  };

  const handleUpdateBlock = (index: number, newData: Record<string, any>) => {
    const currentBlocks = [...page.body];
    currentBlocks[index] = {
      ...currentBlocks[index],
      data: newData,
    };
    updateBlocksWithHistory(currentBlocks);
  };

  const handleTypeChange = (index: number, newType: CmsBlock['type']) => {
    const currentBlocks = [...page.body];
    currentBlocks[index] = {
      ...currentBlocks[index],
      type: newType,
      data: getDefaultBlockData(newType, currentBlocks[index].data),
    };
    updateBlocksWithHistory(currentBlocks);
  };

  const handleMoveBlock = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= page.body.length) return;
    const currentBlocks = [...page.body];
    const [moved] = currentBlocks.splice(fromIndex, 1);
    currentBlocks.splice(toIndex, 0, moved);
    updateBlocksWithHistory(currentBlocks);
  };

  const handleDuplicateBlock = (index: number) => {
    const currentBlocks = [...page.body];
    const target = currentBlocks[index];
    const duplicated: CmsBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: target.type,
      data: JSON.parse(JSON.stringify(target.data)),
    };
    currentBlocks.splice(index + 1, 0, duplicated);
    updateBlocksWithHistory(currentBlocks);
  };

  const handleDeleteBlock = (index: number) => {
    const currentBlocks = page.body.filter((_, i) => i !== index);
    updateBlocksWithHistory(currentBlocks);
  };

  const handleSaveToD1 = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const res = await fetch('/api/pages', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(page),
      });

      if (!res.ok) {
        const errorData: any = await res.json().catch(() => ({}));
        throw new Error(errorData?.error || `Failed to save page (Status ${res.status})`);
      }

      setSaveStatus('Page published and KV cache invalidated successfully!');
      setTimeout(() => setSaveStatus(null), 4000);

      if (onSaveSuccess) {
        onSaveSuccess(page);
      }
    } catch (err: any) {
      alert(`Save error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = async () => {
    if (!window.confirm(`Are you sure you want to delete the page "/${page.slug}"? This cannot be undone.`)) {
      return;
    }

    try {
      const res = await fetch(`/api/pages/${page.slug}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errorData: any = await res.json().catch(() => ({}));
        throw new Error(errorData?.error || `Failed to delete page (Status ${res.status})`);
      }
      if (onDeleteSuccess) {
        onDeleteSuccess(page.slug);
      }
    } catch (e: any) {
      alert(`Error deleting page: ${e.message}`);
    }
  };

  const handleDiscard = () => {
    if (window.confirm('Discard all unsaved edits on this page?')) {
      setPage(initialPage);
      setHistory([initialPage.body || []]);
      setHistoryIndex(0);
      if (onExit) onExit();
    }
  };

  function getDefaultBlockData(type: CmsBlock['type'], existingData?: Record<string, any>): Record<string, any> {
    const base = existingData || {};
    switch (type) {
      case 'header':
        return { text: base.text || '', level: base.level || 2 };
      case 'paragraph':
        return { text: base.text || base.html || '' };
      case 'image':
        return { url: base.url || '', caption: base.caption || '' };
      case 'list':
        return { style: base.style || 'unordered', items: base.items || ['First item'] };
      case 'callout':
        return { variant: base.variant || 'info', title: base.title || '', text: base.text || '' };
      case 'table':
        return {
          content: base.content || [
            ['Header 1', 'Header 2'],
            ['Cell 1', 'Cell 2'],
          ],
        };
      case 'columns':
        return { cols: base.cols || ['', ''] };
      case 'code':
        return { html: base.html || base.code || '' };
      case 'delimiter':
      default:
        return {};
    }
  }

  return (
    <div className="site-editor-container bg-body my-4 rounded-4 shadow-sm border p-3 p-md-4">
      {/* Editor Main Control Toolbar */}
      <div className="editor-top-bar d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 mb-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-primary-subtle text-primary fw-semibold px-2 py-1">
            Site Editor
          </span>
          <h4 className="mb-0 fw-bold">{page.pageName || 'Untitled'}</h4>
          <span className="badge bg-secondary-subtle text-secondary">/{page.slug}</span>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Undo / Redo */}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={historyIndex <= 0}
            onClick={handleUndo}
            title="Undo"
          >
            <Undo size={14} />
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={historyIndex >= history.length - 1}
            onClick={handleRedo}
            title="Redo"
          >
            <Redo size={14} />
          </button>

          {/* Page Settings */}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={() => setIsSettingsOpen(true)}
          >
            <Settings size={14} /> Settings
          </button>

          {/* Preview Toggle */}
          <button
            type="button"
            className={`btn btn-sm d-flex align-items-center gap-1 ${isPreview ? 'btn-info' : 'btn-outline-secondary'}`}
            onClick={() => setIsPreview(!isPreview)}
          >
            {isPreview ? <Edit2 size={14} /> : <Eye size={14} />}
            {isPreview ? 'Edit Mode' : 'Live Preview'}
          </button>

          {/* Discard */}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={handleDiscard}
          >
            <RotateCcw size={14} /> Discard
          </button>

          {/* Save to D1 */}
          <button
            type="button"
            className="btn btn-dark btn-sm d-flex align-items-center gap-1 px-3"
            disabled={saving}
            onClick={handleSaveToD1}
          >
            {saving ? (
              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
            ) : (
              <Save size={14} />
            )}
            {saving ? 'Saving to D1...' : 'Publish / Save'}
          </button>

          {page.slug !== 'index' && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={handleDeletePage}
              title="Delete Page"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {saveStatus && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3 py-2 px-3">
          <CheckCircle2 size={18} />
          <span>{saveStatus}</span>
        </div>
      )}

      {/* Editor Content Area */}
      {isPreview ? (
        <div className="preview-rendered-canvas p-4 border rounded-3 bg-body-tertiary">
          <div className="preview-banner mb-3 small text-secondary fst-italic">
            Live Preview Mode — exact rendering of blocks for visitors
          </div>
          <div className="cms-page-canvas">
            {page.body.length === 0 ? (
              <p className="text-secondary">This page is currently empty. Switch back to Edit Mode to add blocks.</p>
            ) : (
              page.body.map((block, idx) => (
                <RenderCmsBlockPreview key={block.id || idx} block={block} />
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="editor-blocks-list">
          {page.body.length === 0 ? (
            <div className="empty-editor-state p-5 text-center border border-dashed rounded-4 my-3">
              <p className="fw-semibold mb-2">No blocks on this page yet.</p>
              <p className="text-secondary small mb-3">Choose a block type below to begin writing content.</p>
              <BlockToolbar onAddBlock={handleAddBlock} />
            </div>
          ) : (
            <>
              {page.body.map((block, idx) => (
                <BlockItem
                  key={block.id || idx}
                  block={block}
                  index={idx}
                  totalBlocks={page.body.length}
                  onUpdate={(data) => handleUpdateBlock(idx, data)}
                  onTypeChange={(type) => handleTypeChange(idx, type)}
                  onMoveUp={() => handleMoveBlock(idx, idx - 1)}
                  onMoveDown={() => handleMoveBlock(idx, idx + 1)}
                  onDuplicate={() => handleDuplicateBlock(idx)}
                  onDelete={() => handleDeleteBlock(idx)}
                />
              ))}
              <BlockToolbar onAddBlock={handleAddBlock} />
            </>
          )}
        </div>
      )}

      {/* Page Settings Modal */}
      <PageSettingsModal
        isOpen={isSettingsOpen}
        page={page}
        allPages={allPages}
        onClose={() => setIsSettingsOpen(false)}
        onSave={(updated) => setPage((prev) => ({ ...prev, ...updated }))}
      />
    </div>
  );
};

export const RenderCmsBlockPreview: React.FC<{ block: CmsBlock }> = ({ block }) => {
  const d = block.data || {};
  switch (block.type) {
    case 'header': {
      const level = d.level || 2;
      if (level === 1) return <h1 className="cms-heading my-3">{d.text}</h1>;
      if (level === 3) return <h3 className="cms-heading my-3">{d.text}</h3>;
      if (level === 4) return <h4 className="cms-heading my-3">{d.text}</h4>;
      if (level === 5) return <h5 className="cms-heading my-3">{d.text}</h5>;
      if (level === 6) return <h6 className="cms-heading my-3">{d.text}</h6>;
      return <h2 className="cms-heading my-3">{d.text}</h2>;
    }
    case 'paragraph':
      return <div className="cms-paragraph my-3" dangerouslySetInnerHTML={{ __html: d.text || d.html || '' }} />;
    case 'image':
      return (
        <figure className="figure my-3 text-center w-100">
          <img src={d.url || d.file?.url} alt={d.caption || ''} className="figure-img img-fluid rounded max-h-500 shadow-sm" />
          {d.caption && <figcaption className="figure-caption mt-1">{d.caption}</figcaption>}
        </figure>
      );
    case 'list':
      return d.style === 'ordered' ? (
        <ol className="cms-list my-3">
          {(d.items || []).map((it: string, i: number) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: it }} />
          ))}
        </ol>
      ) : (
        <ul className="cms-list my-3">
          {(d.items || []).map((it: string, i: number) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: it }} />
          ))}
        </ul>
      );
    case 'callout':
      return (
        <div className={`alert alert-${d.variant || 'info'} my-3`} role="alert">
          {d.title && <h6 className="alert-heading fw-bold">{d.title}</h6>}
          <div dangerouslySetInnerHTML={{ __html: d.text || '' }} />
        </div>
      );
    case 'table': {
      const rows: string[][] = Array.isArray(d.content) ? d.content : [];
      return (
        <div className="table-responsive my-3">
          <table className="table table-bordered table-striped">
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (
                    <td key={ci} className={ri === 0 ? 'fw-bold bg-body-secondary' : ''} dangerouslySetInnerHTML={{ __html: c }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'columns': {
      const cols: string[] = Array.isArray(d.cols) ? d.cols : [];
      return (
        <div className="row g-3 my-3">
          {cols.map((col, ci) => (
            <div key={ci} className={cols.length === 3 ? 'col-md-4' : 'col-md-6'} dangerouslySetInnerHTML={{ __html: col }} />
          ))}
        </div>
      );
    }
    case 'code':
      return <div className="cms-raw-code my-3" dangerouslySetInnerHTML={{ __html: d.html || d.code || '' }} />;
    case 'delimiter':
    default:
      return <hr className="my-4" />;
  }
};
